import { isAxiosError } from 'axios'
import type { History } from 'history'
import { jwtDecode } from 'jwt-decode'
import queryString from 'query-string'
import { call, put, takeLatest } from 'redux-saga/effects'

import type { Account as apiAccountType } from '@b-stock/account-api-client'
import { IdentityProviderType, RoleStatus } from '@b-stock/account-api-client'
import {
  isAuthCodeNotFoundError,
  type AccessTokenContent,
  type ManagedAccessToken,
  ddLogger,
  interpUri,
  Cookies,
} from '@b-stock/fe-core/bstock-react'

import accountApi from '@api/account'
import signUpApi from '@api/signUp'
import sitesApi from '@api/sites'
import termsApi from '@api/terms'
import appConfig from '@config/config'
import { parseStateObject } from '@helpers/auth/oauthState'
import CookieManager from '@helpers/CookieManager'
import { gtag, gtagFields } from '@helpers/gtag'
import { sanitizeEmail } from '@helpers/sanitize'
import wait from '@helpers/wait'
import { getOrigin, replace } from '@helpers/windowLocation'

import type { CheckAccessTokenPayload } from './actions'
import {
  accessTokenFulfilled,
  accessTokenPending,
  accessTokenRejected,
  fill,
  loaded,
  pending,
  reject,
  migrationPending,
} from './actions'
import types from './types'

export enum AuthErrors {
  NoMarketplaces = 'No Marketplaces',
  DeniedMarketplaces = 'Denied Marketplaces',
  PendingMarketplaces = 'Pending Marketplaces',
  MigrationFailed = 'Migration Failed',
  MigrationTooSlow = 'Migration too slow',
  /**
   * Callback could not be tied to the browser that started it. Deliberately
   * unmapped in `LoginError` so it falls through to the generic message.
   */
  LoginVerificationFailed = 'Login verification failed',
}

type OAuthState = {
  redirectAfterLogin?: string
  redirectAfterSignup?: string
  siteAbb?: string
  subflow?: string
  id?: string
  /**
   * Anti-CSRF nonce. Verified against sessionStorage by the callback page
   * before this saga is ever dispatched, so it is not re-checked here.
   */
  csrf?: string
  /**
   * Login-terms marker, set by the sign-in saga (/signin, or /signup with an
   * existing account's email). Presence gates recording.
   */
  tc?: string
}

type AccountRole = NonNullable<AccessTokenContent['accountRoles']>[string]

const parseOAuthState = (state: string): OAuthState | null =>
  parseStateObject(state)

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'an error has occurred'

function* handleSiteStatus() {
  try {
    yield put(pending())
    yield put(fill())
  } catch (error: unknown) {
    ddLogger.error(
      'Error in handleSiteStatus',
      {},
      error instanceof Error ? error : undefined
    )
  }
}

/**
 * Record acceptance of `config.loginTermsPolicyKey`. Submitting the FusionAuth
 * login form — which showed the consent notice — is the acceptance event.
 *
 * Never throws, so a terms-service outage can't block login; a failed write
 * retries on the next login.
 */
export function* recordTermsAcceptance({
  accountId,
}: {
  accountId: string
}): Generator {
  const policyKey = appConfig.loginTermsPolicyKey
  try {
    const acceptedPolicies = (yield call(termsApi.getAcceptedPolicies, {
      id: accountId,
    })) as Awaited<ReturnType<typeof termsApi.getAcceptedPolicies>>
    // Skip the POST if already accepted: accept is not side-effect free — even
    // a no-op write publishes a terms_accepted event and re-evaluates roles.
    if (!acceptedPolicies[policyKey]) {
      yield call(termsApi.acceptPolicy, { id: accountId, policy: policyKey })
    }
  } catch (error: unknown) {
    if (isAxiosError(error) && error.response?.status === 403) {
      ddLogger.info(
        'Failed to record login-terms acceptance (HTTP 403 - likely bad/missing token)',
        { target: { accountId, policyKey } },
        error
      )
    } else {
      ddLogger.warn(
        'Failed to record login-terms acceptance',
        { target: { accountId, policyKey } },
        error instanceof Error ? error : undefined
      )
    }
  }
}

type LoginResult = Awaited<ReturnType<ManagedAccessToken['login']>>

// One exchange per auth code: codes are single-use, so a second request for
// the same code always fails with auth_code_not_found. Module scope rather
// than sessionStorage so a crashed tab cannot leave a stale entry behind.
const inFlightExchanges = new Map<string, Promise<LoginResult>>()

export const _resetInFlightExchanges = () => {
  inFlightExchanges.clear()
}

export const exchangeAuthCode = (
  managedAccessToken: ManagedAccessToken,
  args: Parameters<ManagedAccessToken['login']>[0] & { code: string }
): Promise<LoginResult> => {
  const inFlight = inFlightExchanges.get(args.code)
  if (inFlight) {
    ddLogger.warn('auth_code_exchange_deduped', { dedup: 'in-flight-promise' })
    return inFlight
  }
  const exchange = managedAccessToken.login(args)
  inFlightExchanges.set(args.code, exchange)
  // Cleared when the request settles, not in the saga's finally: takeLatest
  // cancels the first saga (running its finally) while the request is still
  // in flight, which is exactly when the duplicate arrives.
  const clear = () => {
    inFlightExchanges.delete(args.code)
  }
  exchange.then(clear, clear)
  return exchange
}

/**
 * This Saga gets executed by OAuth2RedirectPage, which is where the user
 * lands after authenticating with an identity provider (currently FusionAuth
 * is the common case, with Salesforce being our identity provider for CSP
 * users)
 * Note: This saga has a few different points where it can stop early by
 * either throwing an exception that gets caught by the try/catch, or by
 * returning
 */
export function* authCodeSaga({
  identityProvider,
  payload,
  history,
  managedAccessToken,
  codeVerifier,
}: {
  identityProvider: IdentityProviderType
  payload: { code?: string; state?: string }
  history: History
  managedAccessToken: ManagedAccessToken
  /**
   * PKCE verifier, already matched against the echoed `state.csrf` by the
   * callback page. Omitted for legacy logins.
   */
  codeVerifier?: string
  type?: string
}): Generator {
  let accountRole: AccountRole | null = null
  let parsedState: OAuthState | null = null
  try {
    yield put(accessTokenPending())

    // Payload is pulled from the query parameters sent by identity provider
    // code = OAuth 2 authorization code which we'll exchange for access token
    // state = state 3mp sent to the identity provider, which then got echo'd
    //         back to 3mp (maybe manipulated along the way, so don't trust it)
    const { code, state } = payload
    if (!code) {
      // No code in query parameters means user is definitely not logged in.
      throw new Error('User not logged in.')
    }

    const { accessToken } = (yield call(exchangeAuthCode, managedAccessToken, {
      code,
      identityProvider,
      // Omit the key entirely when absent so legacy requests stay byte-identical
      ...(codeVerifier !== undefined ? { codeVerifier } : {}),
    })) as LoginResult
    if (!accessToken) {
      throw new Error('login response missing access_token')
    }

    // Parse the state data that we got from FusionAuth (if any)
    // Note: This state could have been manipulated, so don't trust it for
    // anything that the user won't be able to notice and correct later
    if (state) {
      parsedState = parseOAuthState(state)
    }

    // Note: does not use `decodeAccessToken` as the token may not actually be valid yet (magento migration)
    let jwtData: Partial<AccessTokenContent> = jwtDecode(accessToken)

    const email = sanitizeEmail(jwtData.email)
    const siteAbb = parsedState?.siteAbb
    const id = parsedState?.id

    // emit google analytics event
    const ua_id = CookieManager.getCookie(Cookies.BSTOCK_UA_ID)
    gtag({
      event: gtagFields.event.login,
      website_flow: gtagFields.website_flow,
      method: gtagFields.method.fusionAuth,
      user_id: ua_id || null,
      ...(siteAbb ? { marketplace: siteAbb } : {}),
    })

    if (
      !jwtData.accountId &&
      !jwtData.personId &&
      identityProvider === IdentityProviderType.Fusionauth
    ) {
      // The user authenticated, but has no accountId or personId - they must
      // have a Magento account but no 3mp account. This should never happen
      // for anyone but users with FA as their ident provider but we check the
      // IP as this case with a non-FA IP would be an error.

      // Pull the marketplace access data from the Bridge service
      const checkMageExistsData = (yield call(signUpApi.checkMageExists, {
        email,
      })) as Awaited<ReturnType<typeof signUpApi.checkMageExists>>

      // Log a summary of the user's enterprise site statuses
      const statusToSiteMap: Record<string, unknown> = {}
      checkMageExistsData.forEach((site) => {
        statusToSiteMap[site.status] = statusToSiteMap[site.status] ?? site
      })

      ddLogger.info(
        "User logged in, but doesn't have a 3MP account or person id.",
        {
          target: {
            siteAbb,
            email,
          },
          mageExists: statusToSiteMap,
        }
      )

      // Check if user approved on any Marketplace - if they are, let them in
      const approvedOnAny = checkMageExistsData.some(
        (site) => site.status === 'Approved'
      )
      if (!approvedOnAny) {
        // If they aren't approved, they need to wait for approval
        // The messaging we give them varies depending on if they are still
        // Pending or Denied on a Marketplace or not
        const pendingOnAny = checkMageExistsData.some(
          (site) => site.status === 'Pending'
        )
        if (pendingOnAny) {
          yield put(reject({ message: AuthErrors.PendingMarketplaces }))
        } else {
          const deniedOnAny = checkMageExistsData.some(
            (site) => site.status === 'Denied'
          )
          if (deniedOnAny) {
            yield put(reject({ message: AuthErrors.DeniedMarketplaces }))
          } else {
            yield put(reject({ message: AuthErrors.NoMarketplaces }))
          }
        }

        // The user never actually logged in - forget the access tokens so they
        // can try to log in later without issue
        // They'll stay authenticated in FusionAuth, so this shouldn't impact
        // them if they're actively using a Magento marketplace
        yield call([managedAccessToken, 'clearTokenCookies'])
        return
      }

      // They were approved, so tell the bridge service to migrate their account
      const { status: migrateStatus } = (yield call(
        signUpApi.migrateMagentoUser
      )) as Awaited<ReturnType<typeof signUpApi.migrateMagentoUser>>
      if (migrateStatus !== 202) {
        // Something went wrong and the migration wasn't started - bail out
        ddLogger.warn(
          "Call to migration endpoint didn't return the expected status code of 202. Erroring out of migration.",
          {
            migrateStatusCode: migrateStatus,
          }
        )
        yield put(reject({ message: AuthErrors.MigrationFailed }))

        // The user never actually logged in - forget the access tokens so they
        // can try to log in later without issue
        // They'll stay authenticated in FusionAuth, so this shouldn't impact
        // them if they're actively using a Magento marketplace
        yield call([managedAccessToken, 'clearTokenCookies'])
        return
      }

      // Give some feedback to the user that their login is still processing,
      // it just might take longer
      yield put(migrationPending())
      let refreshCount = 0
      while (!jwtData.accountId && !jwtData.personId) {
        refreshCount++
        if (refreshCount > 100) {
          // The migration definitely shouldn't be taking this long... bail out
          // and let the user know they can try later
          ddLogger.warn(
            `Migration doesn't appear to have been finished after ${refreshCount} refresh attempts`
          )
          yield put(reject({ message: AuthErrors.MigrationTooSlow }))

          // The user never actually logged in - forget the access tokens so
          // they can try to log in later without issue
          // They'll stay authenticated in FusionAuth, so this shouldn't impact
          // them if they're actively using a Magento marketplace
          yield call([managedAccessToken, 'clearTokenCookies'])

          return
        }
        // Avoid spamming the refresh by waiting a little before each check
        yield call(wait, 1000)
        const refreshResult = (yield call([
          managedAccessToken,
          'refreshToken',
        ])) as Awaited<ReturnType<ManagedAccessToken['refreshToken']>>
        if (!refreshResult?.accessToken) {
          // Something unexpected went wrong refreshing the token... treat it as if
          // the migration was too slow to ensure the user gets some error feedback
          refreshCount = 9999
        } else {
          jwtData = jwtDecode(refreshResult.accessToken)
        }
      }
      // We've got an accountId and personId now, so the migration must have at
      // least started... but give the back-end a little extra time to finish
      // the migration, just to be safe
      yield call(wait, 5000)
    }

    // Pull some 3mp account data out of the JWT token
    const { accountId, accountRoles } = jwtData
    if (!accountId || !accountRoles) {
      throw new Error(
        'JWT was supposedly valid... but is missing expected data (accountId and/or accountRoles)'
      )
    }
    // accountRoles is a Record, accountId may not exist as a key
    // Default to empty object which intelligentRedirect handles with optional chaining
    accountRole = accountRoles[accountId] ?? {}

    // GLOB-3875: after the Magento-migration block (a first-time migrator's
    // accountId now exists), before any redirect. The provider gate is
    // route-determined (/login ⇒ Fusionauth), so Magento/enterprise logins
    // are included.
    if (
      identityProvider === IdentityProviderType.Fusionauth &&
      // Only the sign-in saga sets `tc`; the passwordless hand-off and
      // settings password reset don't, so they don't record
      parsedState?.tc
    ) {
      yield call(recordTermsAcceptance, { accountId })
    }

    // hot-fix get account data instead of token data due to isComplete race condition
    const account = (yield call(accountApi.fetchAccount, {
      id: accountId,
    })) as Awaited<ReturnType<typeof accountApi.fetchAccount>>
    ddLogger.info('signing in with accountId: ', { accountId })
    // ZRO-2261
    // check jwt from sites obj to see if siteAbb from state exists in sites obj from token
    if (siteAbb) {
      yield call(addMarketplaceToAccount, {
        siteAbb,
        id,
        accountData: account,
        history,
        managedAccessToken,
      })
      return
    }

    yield put(accessTokenFulfilled())

    void intelligentRedirect({ accountRole, parsedState, history })
  } catch (error: unknown) {
    // fe-core's login() does not throw the axios error directly; it throws
    // Error('login response missing access_token', { cause: <axios error> }).
    // Unwrap it so the response checks below see the real HTTP failure.
    const apiError =
      error instanceof Error && error.cause !== undefined ? error.cause : error
    const message = getErrorMessage(error)

    if (isAuthCodeNotFoundError(error)) {
      // The auth code was already consumed by a request this tab could not
      // dedup (another tab, a replayed callback, or the network re-sending
      // the exchange). login() already adopts the winner's cookies when
      // it can see them, so reaching here means the winner had not landed yet
      // when the API answered. Check again now; if a session exists, redirect
      // silently.
      const jwtData = managedAccessToken.currentWithoutWait()?.decoded
      if (jwtData?.accountId && jwtData.accountRoles) {
        accountRole = jwtData.accountRoles[jwtData.accountId] ?? {}
        void intelligentRedirect({ accountRole, parsedState, history })
        return // finally dispatches loaded()
      }
      ddLogger.warn(
        'auth_code_not_found — possible race condition duplicate',
        {},
        error instanceof Error ? error : undefined
      )
      yield put(accessTokenRejected(error))
      yield put(reject({ message }))
      return // finally dispatches loaded()
    }

    ddLogger.error(
      'Error in authCodeSaga',
      {},
      error instanceof Error ? error : undefined
    )
    if (
      isAxiosError(apiError) &&
      apiError.response?.status === 409 &&
      accountRole
    ) {
      return intelligentRedirect({
        accountRole,
        parsedState,
        history,
      })
    }
    yield put(accessTokenRejected(error))
    yield put(reject({ message }))
  } finally {
    yield put(loaded())
  }
}

// Path prefixes `intelligentRedirect` sends through `navigateAwayTo` because
// they belong to another portal's router, not this app's own. Exported so
// OAuth2RedirectPage's isCrossPortalRedirectPath (a replayed-callback variant
// of this same decision) can check against the same list instead of keeping
// its own copy.
const SELL_PATH = '/sell'
const BUY_PATH = '/buy'
const CROSSLISTER_PATH = '/crosslister'
const CSPORTAL_PATH = '/csportal'
const COPS_PATH = '/cops'
const CMS_PORTAL_PATH = '/cms-portal'
const ALL_AUCTIONS_PATH = '/all-auctions'
const AUCTION_SCHEDULE_PATH = '/auction-schedule'

export const CROSS_PORTAL_PATH_PREFIXES = [
  SELL_PATH,
  BUY_PATH,
  CROSSLISTER_PATH,
  CSPORTAL_PATH,
  COPS_PATH,
  CMS_PORTAL_PATH,
  ALL_AUCTIONS_PATH,
  AUCTION_SCHEDULE_PATH,
] as const

/**
 * Exit the sign up process, cleaning up temporary state in the session.
 * Uses window.location.replace instead of assign to prevent the OAuth redirect
 * page from being added to browser history (prevents OAuth code reuse bug).
 */
export const navigateAwayTo = (url: string) => {
  localStorage.removeItem('BStock::AP::SignUp')
  if (url.startsWith('/')) {
    // If it's a relative path, reset to the domain root
    replace(`${getOrigin()}${url}`)
  } else {
    // Otherwise (like full http/https URLs), just replace directly
    replace(url)
  }
}

/**
 * Send the user to the right place.
 * If they tried to access a private route, that route will be set in
 * `parsedState.redirectAfterLogin`. If they didn't, redirectAfterLogin will be
 * undefined and we either send them to seller portal (if they're a seller), to
 * the auctions list (if they're a buyer), or to onboarding if they're neither.
 */
export const intelligentRedirect = async ({
  accountRole,
  parsedState,
  history,
}: {
  accountRole: AccountRole
  parsedState?: OAuthState | null
  history: History
}) => {
  const { redirectAfterLogin, redirectAfterSignup, siteAbb, subflow, id } =
    parsedState ?? {}
  // Warning: redirectAfterLogin (like everything else in parsedState) comes
  // from untrusted sources (the request query parameters), so don't use it
  // where it could pose a security risk (ex: window.location.assign) without
  // sanitizing/validating it first. history.push sanitizes the value for us,
  // so it is safe there (if the value wasn't a valid path, the user will just
  // see the 404 page)
  const redirectPath = redirectAfterSignup || redirectAfterLogin
  const isSeller = accountRole?.seller === RoleStatus.Active
  const isBuyer =
    accountRole?.buyer === RoleStatus.Active ||
    accountRole?.buyer === RoleStatus.Suspended
  const isService = accountRole?.service === RoleStatus.Active

  if (!isSeller && !isBuyer && !isService) {
    // The user isn't an active seller or buyer or service user - assume they
    // haven't completed onboarding (for now)
    if (subflow === 'mao') {
      if (!redirectPath?.startsWith('/buy/')) {
        history.push('/error')
        return
      }
      // MAO subflow skips profile creation - redirect to BP
      navigateAwayTo(redirectPath)
      return
    }
    const qs = queryString.stringify({ redirectAfterLogin, siteAbb })
    history.push(`/create-profile${qs ? `?${qs}` : ''}`)
    return
  }

  // Redirect to exact path if redirect string exists
  if (typeof redirectPath === 'string') {
    if (redirectPath === '/') {
      // Check for root path first
      navigateAwayTo(redirectPath) // Use navigateAwayTo to go to root domain
      return
    } else if (redirectPath.startsWith(ALL_AUCTIONS_PATH)) {
      // Check for /all-auctions path first
      navigateAwayTo(redirectPath) // Use navigateAwayTo to go to all auctions page
      return
    } else if (redirectPath.startsWith(AUCTION_SCHEDULE_PATH)) {
      // Top-level home-portal route (like /all-auctions above), not nested
      // under /buy — falls to the "account portal internal redirect" branch
      // below without this, which pushes it through accounts-portal's own
      // router and picks up its /acct basename.
      navigateAwayTo(redirectPath)
      return
    } else if (redirectPath.startsWith(SELL_PATH)) {
      // Redirect to seller portal if seller or service
      if (isSeller || isService) {
        navigateAwayTo(redirectPath)
        return
      }
    } else if (
      redirectPath.startsWith(BUY_PATH) ||
      redirectPath.startsWith(CROSSLISTER_PATH)
    ) {
      // Redirect to buyer-facing portals if buyer or service
      if (isBuyer || isService) {
        navigateAwayTo(redirectPath)
        return
      }
    } else if (
      // Internal portals...
      // Customer Service Portal
      redirectPath.startsWith(CSPORTAL_PATH) ||
      // Client Ops Portal
      redirectPath.startsWith(COPS_PATH) ||
      // CMS Portal
      redirectPath.startsWith(CMS_PORTAL_PATH)
    ) {
      // Redirect to internal portals only if service
      if (isService) {
        navigateAwayTo(redirectPath)
        return
      }
    } else {
      // Account portal internal redirect
      history.push(redirectPath)
      return
    }
  }

  if (siteAbb) {
    const site = await sitesApi.getSite({ siteAbb })
    let siteUrl = site?.url
    if (!siteUrl) {
      throw new Error(`No site url found for ${siteAbb}`)
    }

    // Walmart (siteAbb `wal`) is unique among our sellers in that they have
    // their own subdomain. To work around cookie sharing complication that
    // creates we need to signal to the magento FE that the user is arriving
    // fresh out of the auth flow, so we append a param. This makes us sad,
    // but on the up side we can remove it once walmart is migrated to 3mp
    // buyer frontend. See ZRO-3360 for more details.
    if (['wal', 'wal-i'].includes(siteAbb)) {
      // Check if URL already has query parameters
      const separator = siteUrl.includes('?') ? '&' : '?'
      siteUrl += `${separator}loginRedir=1`
      navigateAwayTo(siteUrl)
    } else {
      navigateAwayTo(id ? `${siteUrl}/auction/auction/view/id/${id}` : siteUrl)
    }
  } else if (isSeller) {
    navigateAwayTo('/sell')
    return
  } else {
    history.push('/settings')
  }
}

export async function addMarketplaceToAccount({
  siteAbb,
  accountData,
  history,
  id,
  managedAccessToken,
}: {
  siteAbb: string
  history: History
  accountData: apiAccountType
  id?: string
  managedAccessToken: ManagedAccessToken
}) {
  let siteUrl: string | null = null
  let addMarketplaceDataResponse: Awaited<
    ReturnType<typeof sitesApi.addMarketplace>
  > | null = null
  let parsedState: OAuthState = {}
  let accountRole: AccountRole | undefined = undefined
  const tokenData = managedAccessToken.currentWithoutWait()?.decoded
  try {
    if (!tokenData) {
      throw new Error(
        'Could not get token data when adding marketplace to account'
      )
    }
    const { url } = (await sitesApi.getSite({ siteAbb })) ?? {}
    const { sites } = accountData
    // accountRoles is a Record, so accountId may not exist as a key
    accountRole = tokenData.accountRoles?.[tokenData.accountId]
    siteUrl = url ?? null
    if (siteAbb && sites?.[siteAbb] && siteUrl) {
      const site = sites[siteAbb]
      if (site.isComplete) {
        parsedState = { siteAbb, id }
      } else {
        parsedState = {
          siteAbb,
          redirectAfterLogin: interpUri`/success?siteAbb=${siteAbb}${id ? `&id=${id}` : ''}`,
        }
      }
    } else {
      // A buyer who verifies their email but abandons sign-up before setting
      // a password (or otherwise ends up without one - see BP-2290) has no
      // buyer/service role at all. addMarketplace's real backend gate is
      // ACTIVE/INTENDED buyer (or ACTIVE service) only - SUSPENDED is listed
      // here so an already-suspended account isn't mistaken for roleless and
      // sent through createRole again, not because addMarketplace accepts it;
      // a SUSPENDED buyer still 403s below, by design.
      const hasUpdatableRole =
        accountRole?.buyer === RoleStatus.Active ||
        accountRole?.buyer === RoleStatus.Intended ||
        accountRole?.buyer === RoleStatus.Suspended ||
        accountRole?.service === RoleStatus.Active

      if (!hasUpdatableRole) {
        try {
          await managedAccessToken.createRole({
            accountId: tokenData.accountId,
            role: 'buyer',
            params: { status: 'INTENDED', reason: 'SELF_ONBOARDING' },
          })
        } catch (error) {
          ddLogger.warn(
            'Failed to create buyer role before adding marketplace to account',
            { target: { accountId: tokenData.accountId, siteAbb } },
            error as Error
          )
        }
      }

      // ZRO-2262
      // if there's no siteAbb in the jwt's sites obj...
      addMarketplaceDataResponse = await sitesApi.addMarketplace({
        id: tokenData.accountId,
        siteAbb,
      })
      // if statusCode is 201, add siteAbb to success page url and set as
      // redirectAfterLogin
      if (addMarketplaceDataResponse.status === 201) {
        // if the buyer is adding a marketplace BUT they have NOT completed
        // onboarding and their role is INTENDED, add the siteAbb to the
        // parsedState. The FE will save it in state when the user is redirected
        // to /create-profile
        // `!hasUpdatableRole` stands in for "role is now INTENDED" here: if we
        // just created the role above (or addMarketplace succeeded despite it
        // failing), the in-memory `accountRole` is stale - it was read from the
        // token before createRole ran, so it can't reflect the new role.
        parsedState = {
          redirectAfterLogin: interpUri`/success?siteAbb=${siteAbb}`,
          ...(!hasUpdatableRole || accountRole?.buyer === RoleStatus.Intended
            ? { siteAbb }
            : {}),
        }
        await managedAccessToken.refreshToken()
        // if code is 409, site already exists in user's account, the FE should
        // check if isComplete is true
      }
    }

    if (accountRole) {
      await intelligentRedirect({ accountRole, parsedState, history })
    } else {
      // If accountRole is not available, redirect to error page
      history.push('/error')
    }
  } catch (error: unknown) {
    ddLogger.error(
      'Error adding marketplace to account',
      { target: { siteAbb, accountId: tokenData?.accountId } },
      error instanceof Error ? error : undefined
    )
    if (isAxiosError(error) && error.response?.status === 409 && siteUrl) {
      if (addMarketplaceDataResponse?.data.isComplete) {
        parsedState = { siteAbb, id }
      } else {
        parsedState = { redirectAfterLogin: '/business-profile', siteAbb, id }
      }
    } else if (
      isAxiosError(error) &&
      error.response?.status === 403 &&
      accountRole?.buyer === RoleStatus.Suspended
    ) {
      parsedState = { redirectAfterLogin: '/error?reason=suspended' }
    } else {
      parsedState = { redirectAfterLogin: '/error' }
    }
    if (accountRole) {
      return intelligentRedirect({ accountRole, parsedState, history })
    }
    history.push('/error')
    return
  }
}

export function* accessTokenCheckSaga({
  payload: { redirectToLogin, redirectAfterLogin, location },
  history,
  managedAccessToken,
}: {
  payload: CheckAccessTokenPayload
  history: History
  managedAccessToken: ManagedAccessToken
  type?: string
}): Generator {
  try {
    if (location && location.pathname === '/login') {
      yield put(loaded())
      return
    }

    const jwtData = managedAccessToken.currentWithoutWait()?.decoded
    if (!jwtData) {
      ddLogger.info(
        'accessTokenCheckSaga run with no access token (this is normal for unauthenticated users)'
      )
      if (redirectToLogin) {
        history.push(
          interpUri`/signin?redirectAfterLogin=${redirectAfterLogin}`
        )
      }
      return
    }

    yield call(handleSiteStatus)
  } catch (error: unknown) {
    ddLogger.error(
      'Error in accessTokenCheckSaga',
      {},
      error instanceof Error ? error : undefined
    )
    // cookies are not there/are expired
    if (redirectToLogin) {
      history.push(interpUri`/signin?redirectAfterLogin=${redirectAfterLogin}`)
    }
  } finally {
    yield put(loaded())
  }
}

type AuthCodeSagaAction = Parameters<typeof authCodeSaga>[0] & { type: string }

function* onAccessToken(action: AuthCodeSagaAction) {
  yield* authCodeSaga(action)
}

type AccessTokenCheckSagaAction = Parameters<typeof accessTokenCheckSaga>[0] & {
  type: string
}

function* onAccessTokenCheck(action: AccessTokenCheckSagaAction) {
  yield* accessTokenCheckSaga(action)
}

export default function* loginSaga() {
  yield takeLatest(types.ACCESS_TOKEN_FULFILLED, handleSiteStatus)
  yield takeLatest(types.ACCESS_TOKEN, onAccessToken)
  yield takeLatest(types.ACCESS_TOKEN_CHECK, onAccessTokenCheck)
}
